/**
 * HatcheryRegistryPage — Admin Hatchery Registry
 *
 * Calls:  GET /marketplace/vendors  (filters by business_type = hatchery)
 *
 * Displays:
 *   - Hatchery name
 *   - District
 *   - Linked vendor (code)
 *   - Active status
 *   - Summary cards
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
} from '@mui/material';
import {
  SetMeal,
  Search,
  FilterList,
  Refresh,
  Visibility,
  Close,
  CheckCircle,
  Cancel,
  VerifiedUser,
  LocationOn,
  Phone,
  Email,
  Star,
} from '@mui/icons-material';
import { marketplaceAPI } from 'services/api';

// ─── Helpers ────────────────────────────────────────────────
const formatDate = (dt) => {
  if (!dt) return '—';
  return new Date(dt).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

// ─── Status badge ────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const s = (status || '').toLowerCase();
  const cfg = {
    active:               { label: 'Active',  color: 'success' },
    inactive:             { label: 'Inactive', color: 'default' },
    suspended:            { label: 'Suspended', color: 'error' },
    pending_verification: { label: 'Pending',  color: 'warning' },
  };
  const c = cfg[s] || { label: status || 'Unknown', color: 'default' };
  return (
    <Chip
      label={c.label}
      color={c.color}
      size="small"
      variant="outlined"
      sx={{ fontWeight: 600, fontSize: '0.65rem', height: 24 }}
    />
  );
};

// ═════════════════════════════════════════════════════════════
const HatcheryRegistryPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [allVendors, setAllVendors] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  // Detail dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const STATUSES = ['ALL', 'active', 'inactive', 'suspended', 'pending_verification'];

  // ── Load Data ─────────────────────────────────────────────
  const loadData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      setError(null);

      const res = await marketplaceAPI.getVendors();
      const vendors = Array.isArray(res.data) ? res.data : [];
      setAllVendors(vendors);
    } catch (err) {
      console.error('HatcheryRegistry load error:', err);
      setError('Failed to load hatchery data.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Hatcheries = vendors with business_type containing 'hatchery' ──
  const hatcheries = useMemo(() => {
    return allVendors.filter((v) => {
      const bt = (v.business_type || '').toLowerCase();
      const cats = Array.isArray(v.categories) ? v.categories.map((c) => (c || '').toLowerCase()) : [];
      return bt.includes('hatchery') || cats.includes('hatchery') || cats.includes('seed');
    });
  }, [allVendors]);

  // ── Filtered ───────────────────────────────────────────────
  const filtered = useMemo(() => {
    return hatcheries.filter((v) => {
      const matchSearch =
        !search ||
        (v.business_name || '').toLowerCase().includes(search.toLowerCase()) ||
        (v.city || '').toLowerCase().includes(search.toLowerCase()) ||
        (v.vendor_code || '').toLowerCase().includes(search.toLowerCase()) ||
        (v.contact_person || '').toLowerCase().includes(search.toLowerCase());
      const matchStatus =
        statusFilter === 'ALL' || (v.status || '').toLowerCase() === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [hatcheries, search, statusFilter]);

  const activeCount = hatcheries.filter((v) => (v.status || '').toLowerCase() === 'active').length;
  const verifiedCount = hatcheries.filter((v) => v.verified).length;

  // ── District breakdown ─────────────────────────────────────
  const districtCounts = useMemo(() => {
    const map = {};
    hatcheries.forEach((h) => {
      const d = h.city || 'Unknown';
      map[d] = (map[d] || 0) + 1;
    });
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);
  }, [hatcheries]);

  // ── View Detail ────────────────────────────────────────────
  const viewHatchery = async (h) => {
    setDialogOpen(true);
    setSelected(h);
    try {
      setDetailLoading(true);
      const res = await marketplaceAPI.getVendor(h.id);
      setSelected(res.data);
    } catch {
      // keep what we have
    } finally {
      setDetailLoading(false);
    }
  };

  // ── Loading ────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 md:p-6 lg:p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-cyan-100 flex items-center justify-center">
            <SetMeal sx={{ fontSize: 22, color: '#0891b2' }} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Hatchery Registry</h1>
        </div>
        <div className="flex items-center justify-center py-20">
          <CircularProgress size={32} sx={{ color: '#0891b2' }} />
          <span className="ml-3 text-sm text-gray-500">Loading hatchery data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6 lg:p-8">
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl bg-cyan-100 flex items-center justify-center">
              <SetMeal sx={{ fontSize: 22, color: '#0891b2' }} />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Hatchery Registry</h1>
          </div>
          <p className="text-sm text-gray-500 ml-[52px]">
            Registry of approved hatcheries, species availability, and compliance status
          </p>
        </div>
        <Tooltip title="Refresh data">
          <IconButton
            onClick={() => loadData(true)}
            disabled={refreshing}
            sx={{ backgroundColor: '#f1f5f9', '&:hover': { backgroundColor: '#e2e8f0' } }}
          >
            <Refresh
              sx={{
                fontSize: 20,
                animation: refreshing ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } },
              }}
            />
          </IconButton>
        </Tooltip>
      </div>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }} action={<Button size="small" onClick={() => loadData(true)}>Retry</Button>}>
          {error}
        </Alert>
      )}

      {refreshing && <LinearProgress sx={{ mb: 2, borderRadius: 2 }} />}

      {/* ── Summary Cards ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="rounded-2xl bg-cyan-50 border border-cyan-100 p-5">
          <p className="text-xs text-cyan-600 font-medium mb-1">Total Hatcheries</p>
          <p className="text-2xl font-bold text-cyan-800">{hatcheries.length}</p>
          <p className="text-xs text-cyan-500 mt-1">Registered in system</p>
        </div>
        <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-5">
          <p className="text-xs text-emerald-600 font-medium mb-1">Active</p>
          <p className="text-2xl font-bold text-emerald-800">{activeCount}</p>
          <p className="text-xs text-emerald-500 mt-1">Currently operational</p>
        </div>
        <div className="rounded-2xl bg-indigo-50 border border-indigo-100 p-5">
          <p className="text-xs text-indigo-600 font-medium mb-1">Verified</p>
          <p className="text-2xl font-bold text-indigo-800">{verifiedCount}</p>
          <p className="text-xs text-indigo-500 mt-1">
            {hatcheries.length > 0 ? `${((verifiedCount / hatcheries.length) * 100).toFixed(0)}%` : '0%'} compliance
          </p>
        </div>
        <div className="rounded-2xl bg-purple-50 border border-purple-100 p-5">
          <p className="text-xs text-purple-600 font-medium mb-1">Districts Covered</p>
          <p className="text-2xl font-bold text-purple-800">{districtCounts.length}</p>
          <p className="text-xs text-purple-500 mt-1">Geographic spread</p>
        </div>
      </div>

      {/* ── District Breakdown (horizontal chips) ── */}
      {districtCounts.length > 0 && (
        <div className="mb-6 rounded-2xl border border-gray-200 bg-white p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">District Distribution</h3>
          <div className="flex flex-wrap gap-2">
            {districtCounts.map(([district, count]) => (
              <Chip
                key={district}
                icon={<LocationOn sx={{ fontSize: 14 }} />}
                label={`${district} (${count})`}
                variant="outlined"
                size="small"
                sx={{ fontWeight: 500, fontSize: '0.7rem' }}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Table ── */}
      <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
        <div className="p-5 border-b border-gray-100">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <TextField
              placeholder="Search by name, district, or code..."
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 18, color: '#9ca3af' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 280 }}
            />

            <div className="flex items-center gap-1 flex-wrap">
              <FilterList sx={{ fontSize: 16, color: '#9ca3af', mr: 0.5 }} />
              {STATUSES.map((s) => (
                <Chip
                  key={s}
                  label={s === 'ALL' ? 'All' : s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                  variant={statusFilter === s ? 'filled' : 'outlined'}
                  color={statusFilter === s ? 'primary' : 'default'}
                  size="small"
                  onClick={() => setStatusFilter(s)}
                  sx={{ fontWeight: 500, fontSize: '0.65rem', cursor: 'pointer' }}
                />
              ))}
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-3">
            Showing {filtered.length} of {hatcheries.length} hatcheries
            {hatcheries.length === 0 && allVendors.length > 0 && (
              <span> — no vendors have business_type "hatchery". Try adding vendors with this type.</span>
            )}
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left">
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Hatchery Name</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">District</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Linked Vendor</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Contact</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Active Status</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Verified</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Rating</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Registered</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-center">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-4 py-12 text-center text-gray-400 text-sm">
                    {hatcheries.length === 0
                      ? 'No hatcheries found. Register vendors with business type "hatchery" to populate this registry.'
                      : 'No hatcheries match your filters'}
                  </td>
                </tr>
              ) : (
                filtered.map((h) => (
                  <tr key={h.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-800">{h.business_name}</p>
                      {h.description && (
                        <p className="text-xs text-gray-400 truncate max-w-[200px]">{h.description}</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <LocationOn sx={{ fontSize: 14, color: '#9ca3af' }} />
                        <span className="text-sm text-gray-700">{h.city || '—'}</span>
                      </div>
                      {h.state && <p className="text-xs text-gray-400 ml-[18px]">{h.state}</p>}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-mono text-gray-500">{h.vendor_code}</span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-gray-700">{h.contact_person}</p>
                      <p className="text-xs text-gray-400">{h.phone}</p>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={h.status} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      {h.verified ? (
                        <Tooltip title="Verified">
                          <VerifiedUser sx={{ fontSize: 18, color: '#10b981' }} />
                        </Tooltip>
                      ) : (
                        <Tooltip title="Not Verified">
                          <Cancel sx={{ fontSize: 18, color: '#d1d5db' }} />
                        </Tooltip>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {h.average_rating > 0 ? (
                        <div className="flex items-center gap-1">
                          <Star sx={{ fontSize: 14, color: '#f59e0b' }} />
                          <span className="text-sm font-medium">{parseFloat(h.average_rating).toFixed(1)}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {formatDate(h.created_at)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => viewHatchery(h)}>
                          <Visibility sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Detail Dialog ── */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <span className="text-lg font-bold">Hatchery Details</span>
          <IconButton size="small" onClick={() => setDialogOpen(false)}>
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <div className="flex items-center justify-center py-8">
              <CircularProgress size={28} />
            </div>
          ) : selected ? (
            <div className="space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{selected.business_name}</h3>
                  {selected.legal_name && (
                    <p className="text-xs text-gray-400">{selected.legal_name}</p>
                  )}
                  <p className="text-xs text-gray-400 font-mono mt-1">{selected.vendor_code}</p>
                </div>
                <StatusBadge status={selected.status} />
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="p-3 rounded-xl bg-gray-50">
                  <p className="text-xs text-gray-400 mb-1">District</p>
                  <p className="font-medium text-gray-700">{selected.city || '—'}</p>
                  {selected.state && <p className="text-xs text-gray-500">{selected.state}</p>}
                </div>
                <div className="p-3 rounded-xl bg-gray-50">
                  <p className="text-xs text-gray-400 mb-1">Contact</p>
                  <p className="font-medium text-gray-700">{selected.contact_person}</p>
                  <p className="text-xs text-gray-500">{selected.phone}</p>
                </div>
              </div>

              <div className="p-3 rounded-xl bg-gray-50">
                <p className="text-xs text-gray-400 mb-1">Address</p>
                <p className="text-sm text-gray-700">{selected.address}</p>
                {selected.pincode && <p className="text-xs text-gray-500 mt-1">PIN: {selected.pincode}</p>}
              </div>

              {/* Categories / Species */}
              {Array.isArray(selected.categories) && selected.categories.length > 0 && (
                <div>
                  <p className="text-xs text-gray-400 mb-2">Categories / Species</p>
                  <div className="flex flex-wrap gap-1">
                    {selected.categories.map((cat, i) => (
                      <Chip
                        key={i}
                        label={(cat || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                        size="small"
                        variant="outlined"
                        color="primary"
                        sx={{ fontSize: '0.65rem', height: 22 }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Compliance */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-xl bg-gray-50">
                  <p className="text-xs text-gray-400 mb-1">GST</p>
                  <p className="text-sm font-mono text-gray-700">{selected.gst_number || '—'}</p>
                </div>
                <div className="p-3 rounded-xl bg-gray-50">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-gray-400 mb-1">Verification</p>
                    {selected.verified ? (
                      <VerifiedUser sx={{ fontSize: 14, color: '#10b981' }} />
                    ) : (
                      <Cancel sx={{ fontSize: 14, color: '#d1d5db' }} />
                    )}
                  </div>
                  <p className="text-sm text-gray-700">
                    {selected.verified ? `Verified ${formatDate(selected.verified_at)}` : 'Not Verified'}
                  </p>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="p-3 rounded-xl bg-cyan-50">
                  <p className="text-xl font-bold text-cyan-800">{selected.total_orders || 0}</p>
                  <p className="text-xs text-cyan-500">Orders</p>
                </div>
                <div className="p-3 rounded-xl bg-emerald-50">
                  <p className="text-xl font-bold text-emerald-800">{selected.total_ratings || 0}</p>
                  <p className="text-xs text-emerald-500">Ratings</p>
                </div>
                <div className="p-3 rounded-xl bg-amber-50">
                  <p className="text-xl font-bold text-amber-800">
                    {selected.average_rating > 0 ? parseFloat(selected.average_rating).toFixed(1) : '—'}
                  </p>
                  <p className="text-xs text-amber-500">Avg Rating</p>
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} variant="outlined" size="small" sx={{ borderRadius: 2, textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default HatcheryRegistryPage;
